import { Controller } from "@hotwired/stimulus"

// Puts a value on the clipboard: a password reset link, a Slack Request URL, a Slack app
// manifest, or a compact ID chip on the info rail (ticket key, Linear identifier).
//
// Generalized from the reset-link controller it used to be. The behaviour that was specific to that
// one caller — copying and scrolling the moment the element appears — is now behind `auto`, because
// only that caller wants it: the reset link is something an admin just asked to be HANDED, so making
// them click again would be the feature not doing its job. A Slack panel is persistent state, and
// copying on sight would hijack focus, scroll the modal, and silently overwrite the clipboard every
// time the settings modal is opened.
//
// A rail ID chip is the compact caller: it has a `label` target (the identifier itself) rather than
// a visible field. Selecting a visually-hidden input on every successful copy would steal focus and
// scroll the pane, so that path writes first and only reveals the field when the clipboard is gone.
//
// The clipboard is not guaranteed to be there: navigator.clipboard is undefined outside a secure
// context, and the write can be refused even where it exists. So the value is focused and selected
// regardless — that leaves the user one keystroke from the same result.
//
// The one thing this must never do is claim "Copied" when the write did not land.
export default class extends Controller {
  // `url` is a one-line input; `block` is a <pre> holding something multi-line. A settings caller
  // has one or the other. A rail chip has `url` too, but hidden, plus `label` for the ID it shows.
  static targets = ["url", "block", "button", "status", "label"]
  static values = { url: String, auto: Boolean, restLabel: String }

  static COPIED_LABEL = "Copied"
  static REST_LABEL = "Copy"
  static REVERT_DELAY = 2000

  connect() {
    if (!this.autoValue) return

    this.selectValue()
    // Focusing the field scrolls only the field into view. The block is three rows tall and a row
    // near the bottom of the scrolling settings pane would leave the hint below the fold.
    //
    // "end", not "nearest": nearest scrolls the minimum, so once the field is visible it stops and
    // leaves the hint sheared off by the pane's bottom edge — measured at 7px on the last row of a
    // nine-user list. That line carries the expiry, and in the no-clipboard case it is the only
    // thing telling the user how to copy at all, so it is the part that must not be cut.
    this.element.scrollIntoView({ block: "end", behavior: "instant" })
    this.write()
  }

  disconnect() {
    clearTimeout(this.revertTimeout)
  }

  copy() {
    // A hidden fallback field must not take focus on the happy path — that scrolls the rail.
    if (!this.isCompact) this.selectValue()
    this.write()
  }

  write() {
    if (!navigator.clipboard?.writeText) return this.reportUnavailable()

    navigator.clipboard.writeText(this.urlValue)
      .then(() => this.reportCopied())
      .catch(() => this.reportUnavailable())
  }

  // An input selects itself; a <pre> cannot, so its text is selected through a Range. Both leave the
  // value highlighted and one keystroke from the clipboard, which is the whole point of doing this
  // even when the write succeeded.
  selectValue() {
    if (this.hasUrlTarget) {
      this.urlTarget.focus()
      this.urlTarget.select()
      return
    }
    if (!this.hasBlockTarget) return

    this.blockTarget.focus()
    const range = document.createRange()
    range.selectNodeContents(this.blockTarget)
    const selection = window.getSelection()
    selection.removeAllRanges()
    selection.addRange(range)
  }

  // The button and the hint say deliberately different things, and only the button is transient.
  // The button is an action — "Copied" is momentary feedback that the press landed, and it has to
  // go back to naming what pressing it does. The hint is state: the value IS on the clipboard, and
  // stays there, so clearing that after two seconds would retract something still true.
  //
  // A rail chip is the exception to that last sentence: there is no permanent hint line, so both
  // the label and the live region revert. Claiming the value is still on the clipboard after the
  // button has gone back to showing the ID would be a caption with nowhere to live.
  reportCopied() {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this.isCompact ? this.constructor.COPIED_LABEL : "Copied to clipboard · "
      this.statusTarget.classList.remove("is-hint")
    }
    this.copiedSurface.textContent = this.constructor.COPIED_LABEL
    this.buttonTarget.classList.add("is-on")

    clearTimeout(this.revertTimeout)
    this.revertTimeout = setTimeout(() => this.reportAtRest(), this.constructor.REVERT_DELAY)
  }

  // Not an error: nothing has gone wrong that the user can fix, and the value is right there,
  // selected. It is an instruction, so it is muted like the rest of the hint.
  reportUnavailable() {
    this.revealFallback()
    if (this.hasStatusTarget) {
      const suffix = this.isCompact ? "" : " · "
      this.statusTarget.textContent = `Press ${this.copyShortcut()} to copy${suffix}`
      this.statusTarget.classList.add("is-hint")
    }
    this.reportAtRest()
  }

  reportAtRest() {
    this.copiedSurface.textContent = this.restLabel
    this.buttonTarget.classList.remove("is-on")
    if (this.isCompact && this.hasStatusTarget && !this.statusTarget.classList.contains("is-hint")) {
      this.statusTarget.textContent = ""
    }
  }

  // Compact chips keep the copy icon on the button, so only the identifier span may be rewritten.
  // Settings buttons are the word "Copy" and nothing else, so the button itself is the surface.
  get copiedSurface() {
    return this.hasLabelTarget ? this.labelTarget : this.buttonTarget
  }

  get restLabel() {
    return this.restLabelValue || this.constructor.REST_LABEL
  }

  get isCompact() {
    return this.hasLabelTarget
  }

  revealFallback() {
    if (!this.hasUrlTarget) return

    this.urlTarget.classList.add("is-revealed")
    this.urlTarget.removeAttribute("aria-hidden")
    this.urlTarget.removeAttribute("tabindex")
    this.selectValue()
  }

  copyShortcut() {
    return /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘C" : "Ctrl+C"
  }
}
